import { FilterContainer, MultiSelect } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Switch } from 'antd'
import useFeatureFlag from 'hooks/useFeatureFlags'
import { capitalize } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useShiftTypesList } from 'screens/Rostering/hooks/useShiftTypesList'
import {
  type RotaBuilderFiltersType,
  RotaInstanceStatus
} from 'screens/Rostering/types'
import styled from 'styled-components'
import EventIcon from './EventIcon'
import { FilterChips } from './FilterChips'

type ShiftBuilderFiltersProps = {
  testId?: string
  'data-testid'?: string
  value?: RotaBuilderFiltersType
  onChange?: (value: RotaBuilderFiltersType) => void
  publishedRota?: boolean
}

type ActiveFilter = {
  key: 'typeOfEvent' | 'status' | 'warnings' | 'allocations'
  value: string[]
}

export const ShiftBuilderFilters = ({
  testId,
  'data-testid': dataTestId,
  value,
  onChange,
  publishedRota
}: ShiftBuilderFiltersProps) => {
  const baseId = testId || dataTestId || 'shift-builder-filters'
  const { t } = useTranslation()

  const { shiftTypes } = useShiftTypesList()

  const flags = useFeatureFlag({
    flagNames: ['rostering_dev_features', 'rostering_parked_features']
  })?.data

  const flagsRosteringDevFeaturesEnabled =
    flags?.rostering_dev_features?.enabled

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  const handleInputChange = (newValue: Partial<RotaBuilderFiltersType>) => {
    const updatedValue: RotaBuilderFiltersType = {
      ...(value || {}),
      ...newValue
    }
    onChange?.(updatedValue)
  }

  const handleRemoveFilter = (key: string, valueToRemove: string) => {
    const filterKey = key as keyof RotaBuilderFiltersType
    if (!value?.[filterKey]) return

    const newValue = { ...value }

    switch (filterKey) {
      case 'status':
        if (Array.isArray(value.status)) {
          newValue.status = value.status.filter(
            (v) => v.toLowerCase() !== valueToRemove.toLowerCase()
          )
        }
        break
      case 'typeOfEvent':
        if (Array.isArray(value.typeOfEvent)) {
          const shiftType = shiftTypes?.find(
            (shift) => shift.name === valueToRemove
          )
          newValue.typeOfEvent = value.typeOfEvent.filter(
            (v) => v !== shiftType?.id
          )
        }
        break
      case 'warnings':
      case 'allocations':
        // @ts-ignore
        if (Array.isArray(value[filterKey])) {
          // @ts-ignore
          newValue[filterKey] = value[filterKey].filter(
            (v) => v !== valueToRemove
          )
        }
        break
      default:
        throw new Error(`Unexpected filter key: ${filterKey}`)
    }

    onChange?.(newValue)
  }

  const handleClearAll = () => {
    onChange?.({})
  }

  const getActiveFilters = (): ActiveFilter[] => {
    if (!value) return []

    return Object.entries(value)
      .map(([key, values]) => {
        if (!Array.isArray(values) || !values.length) {
          return null
        }

        switch (key) {
          case 'typeOfEvent':
            return {
              key,
              value: values.map((v) => {
                const shiftType = shiftTypes?.find((shift) => shift.id === v)
                return shiftType?.name || v
              })
            }
          case 'status':
            return {
              key,
              value: values.map((v) => capitalize(v))
            }
          case 'warnings':
          case 'allocations':
            return {
              key,
              value: values
            }
          default:
            return null
        }
      })
      .filter((filter): filter is ActiveFilter => filter !== null)
  }

  const instanceStatusOptions = Object.values(RotaInstanceStatus).map(
    (value) => ({
      label: capitalize(value),
      value,
      image: <EventIcon status={value} />
    })
  )

  const warnings = [
    'Double booked',
    'Skills not matched',
    'Gender not matched',
    'No warnings'
  ]

  const warningOptions = warnings.map((item) => ({
    label: item,
    value: item
  }))

  const allocationOptions = [
    { value: 'Unallocated', label: t('UNALLOCATED') },
    { value: 'Partially Allocated', label: t('PARTIALLY_ALLOCATED') },
    { value: 'Allocated', label: t('FULLY_ALLOCATED') }
  ]

  const shiftOptions = shiftTypes?.map((shift) => ({
    label: shift.name,
    value: shift.id
  }))

  return (
    <FilterContainer title={t('FILTERS')}>
      <>
        <Filters>
          <LeftContainer>
            <StyledWideMultiSelect
              values={value?.typeOfEvent}
              title={t('TYPE_OF_SHIFT')}
              options={shiftOptions}
              hasClear
              hasSearch
              hasSelectAll
              onChange={(selectedValues) =>
                handleInputChange({
                  typeOfEvent: selectedValues as string[]
                })
              }
            />
            <StyledMultiSelect
              values={value?.status}
              title={t('EVENT_STATUS')}
              options={
                publishedRota
                  ? instanceStatusOptions.filter(
                      (status) => status.value !== RotaInstanceStatus.DRAFT
                    )
                  : instanceStatusOptions
              }
              hasClear
              hasSearch
              hasSelectAll
              onChange={(selectedStatus) =>
                handleInputChange({
                  status: selectedStatus as RotaInstanceStatus[]
                })
              }
            />
            <StyledMultiSelect
              values={value?.allocations}
              title={t('ALLOCATIONS')}
              options={allocationOptions}
              hasClear
              hasSearch
              hasSelectAll
              onChange={(selectedValues) =>
                handleInputChange({
                  allocations: selectedValues as string[]
                })
              }
            />
            {flagsRosteringParkedFeaturesEnabled && (
              <StyledWideMultiSelect
                values={value?.warnings}
                title={t('WARNINGS')}
                options={warningOptions}
                hasClear
                hasSearch
                hasSelectAll
                onChange={(selectedValues) =>
                  handleInputChange({
                    warnings: selectedValues as string[]
                  })
                }
              />
            )}
          </LeftContainer>
          {flagsRosteringDevFeaturesEnabled && (
            <RightContainer>
              <SpotlightText weight="medium">{t('SPOTLIGHT')}</SpotlightText>
              <Switch />
            </RightContainer>
          )}
        </Filters>
        {Object.values(value || {}).some(
          (arr) => Array.isArray(arr) && arr.length > 0
        ) && (
          <FilterChips
            filters={getActiveFilters()}
            onRemove={handleRemoveFilter}
            onClearAll={handleClearAll}
          />
        )}
      </>
    </FilterContainer>
  )
}

const LeftContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledMultiSelect = styled(MultiSelect)`
  min-width: 160px;
`

const StyledWideMultiSelect = styled(MultiSelect)`
  min-width: 200px;
`

const Filters = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

const SpotlightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`
